import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Area, AreaChart, CartesianGrid,
  LabelList, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCampaigns          as getCampaigns,
  getDashboardPtpSummaryHourly   as getPtpSummary,
  getDashboardPtpByDpdHourly     as getPtpByDpd,
  getDashboardPtpByDayHourly     as getPtpByDay,
  getDashboardPtpByDpdDayHourly  as getPtpByDpdDay,
  getDashboardAccountsHourly     as getAccounts,
  // PTP $ Total/Avg/Max/Min. summary_ptp_hourly carries SUMS only — no
  // per-event max or min — so the KPI row reads the payments route, which
  // aggregates raw customerconvevent and does expose them.
  getDashboardPaymentsSummary    as getPaymentsSummary,
} from "../../services/pbi/pbiservices.js";
import {
  useClientTimeZone, zonedNow, zoneAbbr,
} from "../../utils/clienttimezone";

// Targets are hidden; the editor is kept wired but not rendered.
// eslint-disable-next-line no-unused-vars
import PtpTargetEditor from "./PtpTargetEditor.jsx";

// ── Constants ──────────────────────────────────────────────────────
const BLUE     = "#378ADD";
const GREEN    = "#1D9E75";
const MINT     = "#3DBE92";
const AMBER    = "#BA7517";
const PINK     = "#D4537E";
const PURPLE   = "#7F77DD";
const OPTOUT_C = "#A32D2D";
const TARGET_C = "#6B5B95";
// Dotted average rule and the figure beside each chart title. A hue no series
// uses, so the annotation never reads as another measurement.
const AVG_INK  = "#0F6E56";

// ── ONE COLOUR PER METRIC ──────────────────────────────────────────
// A metric reads the same hue wherever it appears: its funnel bar, the figure
// on its KPI tile, its line, its area, its bar, its table column.
//
// Before this the same funnel had RPC and Kept both green and Sent and Kept
// both blue, and the same metric took a different hue on each page — Kept was
// green here, blue on Payments, deep blue on the 24-hr page. Colour was
// decoration; now it identifies.
//
// Chosen for separation rather than family. Kept is a component of PTP and
// Uncommitted is the rest of it, but shading them as siblings of one hue
// makes them hard to tell apart in a legend, which is the one place they are
// read side by side.
const C_SENT        = "#0C447C";  // navy
const C_ACCOUNTS    = "#378ADD";  // blue
const C_RPC         = "#1D9E75";  // green
const C_PTP         = "#BA7517";  // amber
const C_KEPT        = "#7F77DD";  // purple
const C_SL          = "#D4537E";  // pink
const C_UNCOMMITTED = "#3DBE92";  // mint
const C_24HR        = "#A01F50";  // deep pink


const GRAN_OPTIONS  = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

const DPD_BUCKETS = ["1-9","10-19","20-29","30-39","40-49","50-59","60-89","90+"];
const DPD_COLORS = {
  "1-9":   MINT,
  "10-19": GREEN,
  "20-29": BLUE,
  "30-39": "#185FA5",
  "40-49": AMBER,
  "50-59": "#D4870F",
  "60-89": PINK,
  "90+":   "#A01F50",
};

function weightedTarget(rows, key) {
  let weightedSum = 0;
  let totalSent   = 0;
  for (const r of rows || []) {
    const sent   = safeNum(r.texts_sent);
    const target = safeNum(r[key]);
    if (sent <= 0) continue;
    weightedSum += target * sent;
    totalSent   += sent;
  }
  return totalSent > 0 ? weightedSum / totalSent : 0;
}

function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

// PTP $ across the window: Kept + promises, from the payments route's daily
// rows. Max and Min are the widest single event of either kind — the max of
// the two maxima, not their sum, because they describe one event each.
// ── FUNNEL MONEY STEPS COME FROM THE PAYMENTS ROUTE ────────────────
// PTP, Kept and Smart Links are read from /dashboard/payments-summary, which
// aggregates raw customerconvevent, rather than from summary_ptp_hourly.
//
// The two disagree. The summary table is built by a refresh proc and can lag
// or drift from the events it was built from, and the Payments dashboard
// already reports the raw figures — so a funnel sourced from the summary
// showed a different PTP than the page next to it for the same range. One of
// them had to win, and the one closer to the events is the one to trust.
//
// Sent, Accounts and RPC have no counterpart in that route and still come
// from their own sources.
//
// Falls back to the summary when the payments call returns nothing, so a
// failed fetch degrades to the previous numbers rather than an empty funnel.
function paymentsTotals(rows) {
  const t = (rows || []).reduce((a, r) => ({
    kept:       a.kept       + safeNum(r.payment_count),
    keptAmt:    a.keptAmt    + safeNum(r.payment_total),
    promise:    a.promise    + safeNum(r.promise_count),
    promiseAmt: a.promiseAmt + safeNum(r.promise_total),
    sl:         a.sl         + safeNum(r.smartlink_count),
    slAmt:      a.slAmt      + safeNum(r.smartlink_total),
  }), { kept: 0, keptAmt: 0, promise: 0, promiseAmt: 0, sl: 0, slAmt: 0 });
  return {
    ...t,
    ptp:    t.kept + t.promise,
    ptpAmt: t.keptAmt + t.promiseAmt,
    has:    (rows || []).length > 0,
  };
}

function ptpAmountStats(rows) {
  const list  = rows || [];
  const count = list.reduce((a, r) => a + safeNum(r.payment_count) + safeNum(r.promise_count), 0);
  const total = list.reduce((a, r) => a + safeNum(r.payment_total) + safeNum(r.promise_total), 0);
  const maxes = list.flatMap(r => [safeNum(r.payment_max), safeNum(r.promise_max)]).filter(v => v > 0);
  const mins  = list.flatMap(r => [safeNum(r.payment_min), safeNum(r.promise_min)]).filter(v => v > 0);
  return {
    count, total,
    avg: count > 0 ? total / count : 0,
    max: maxes.length ? Math.max(...maxes) : 0,
    min: mins.length  ? Math.min(...mins)  : 0,
  };
}

// ── AVERAGE RULE + HEADER FIGURE ───────────────────────────────────
// The two rules the scorecard uses, for the same reasons.
//
// activeMean — mean over buckets WITH ACTIVITY only. A day the system was not
// running is not a zero; counting it drags the rule down and makes every real
// day look above trend.
//
// pooledPct — for a RATE, total numerator over total denominator. NOT the mean
// of the per-bucket percentages, which weights a 40-send day the same as a
// 4,000-send one; on a book whose volume moves the two differ by multiples.
function activeMean(rows, key) {
  const active = (rows || []).filter(r => safeNum(r[key]) > 0);
  return active.length
    ? active.reduce((a, r) => a + safeNum(r[key]), 0) / active.length
    : 0;
}

function pooledPct(rows, numKey, denKey) {
  const den = (rows || []).reduce((a, r) => a + safeNum(r[denKey]), 0);
  return den
    ? ((rows || []).reduce((a, r) => a + safeNum(r[numKey]), 0) * 100) / den
    : 0;
}

function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  // Switch to M only once the K form would need 4 digits — i.e. once
  // it rounds to 1000.0K or more.
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function fmtPct(v, dec = 2) {
  const n = Number(v);
  if (isNaN(n)) return "—";
  return `${n.toFixed(dec)}%`;
}

function fmtDate(val) {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

function fmtAxisDate(val, gran = "day") {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return String(val);
  if (String(gran).toLowerCase() === "month")
    return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  const wd = ["Su","Mo","Tu","We","Th","Fr","Sa"][d.getDay()];
  const md = d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
  return `${wd} ${md}`;
}

// Still resolved client-side even though the API now buckets by client zone:
// the range boundaries ("today", "this week") must be computed in the
// tenant's zone before being sent, or a range picked at 11pm PT would resolve
// against the wrong "today".
function getDateRange(preset, timeZone) {
  const now = zonedNow(timeZone);
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":      start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":      start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":      start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":     start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":     start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":    start = thisMonday; end = now; break;
    case "CW+Last": start = lastMonday; end = now; break;
    case "This Mo": start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:        start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(start), endDate: toLocal(end) };
}

function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start = parse(startDate);
  const end   = parse(endDate);
  const durMs = end.getTime() - start.getTime();
  const dayMs = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

function KpiTile({ label, count, amount, percent, color = BLUE, delta, target }) {
  const hasPercent = percent !== null && percent !== undefined;
  const hasCount   = count   !== null && count   !== undefined;
  const hasAmount  = amount  !== null && amount  !== undefined;

  let heroValue;
  let heroIsAmount = false;
  if      (hasPercent) heroValue = percent;
  else if (hasCount)   heroValue = count;
  else if (hasAmount)  { heroValue = amount; heroIsAmount = true; }
  else                 heroValue = "—";

  const heroColor       = (hasPercent || hasCount || hasAmount) ? color : "text.disabled";
  const showAmountRow   = hasAmount && !heroIsAmount;
  const showFooterCount = hasCount && hasPercent;

  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2,
      p: "10px 12px",
      minWidth: 0,
      display: "flex", flexDirection: "column", gap: "6px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>

      <Box sx={{ minHeight: 26 }}>
        <Typography sx={{
          fontSize: 20, fontWeight: 500, color: heroColor, lineHeight: 1.2,
        }}>
          {heroValue}
        </Typography>
      </Box>

      <Box sx={{ minHeight: 22 }}>
        {showAmountRow ? (
          <Typography sx={{
            fontSize: 15, fontWeight: 500, color: "text.primary", lineHeight: 1.2,
          }}>
            {amount}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: 15, color: "text.disabled", lineHeight: 1.2,
          }}>
            —
          </Typography>
        )}
      </Box>

      <Box sx={{ minHeight: 18 }}>
        {showFooterCount ? (
          <Typography sx={{
            fontSize: 12, fontWeight: 500, color, lineHeight: 1.2,
          }}>
            {count}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: 12, color: "text.disabled", lineHeight: 1.2,
          }}>
            —
          </Typography>
        )}
      </Box>

      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.25 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}

      {target && target.value > 0 && (
        <KpiTargetLine actual={target.actual} target={target.value} />
      )}
    </Box>
  );
}

function KpiTargetLine({ actual, target }) {
  const a = safeNum(actual);
  const t = safeNum(target);
  if (t === 0) return null;
  const gap = a - t;
  const ratio = a / t;
  const color = ratio >= 1     ? GREEN
              : ratio >= 0.80  ? AMBER
              :                  PINK;
  const sign = gap >= 0 ? "+" : "";
  return (
    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
      Target: <span style={{ color: TARGET_C, fontWeight: 500 }}>{fmtPct(t)}</span>
      {" "}
      <span style={{ color, fontWeight: 500 }}>Δ {sign}{gap.toFixed(1)}</span>
    </Typography>
  );
}

// Small label/value card — the KPI row. No hero hierarchy, no delta: counts
// and rates are in the funnel, so this row is only the shape of the amounts.
function MiniStat({ label, value, color = BLUE }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "8px 12px", minWidth: 0,
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 16, fontWeight: 500, color, lineHeight: 1.2,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, right, children, height = 200 }) {
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
    }}>
      {(title || right) && (
        <Box sx={{ display: "flex", alignItems: "baseline",
                   justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            {title}
          </Typography>
          {right && (
            <Typography sx={{ fontSize: 11, color: AVG_INK, whiteSpace: "nowrap" }}>
              {right}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ width: "100%", height: height === "auto" ? "auto" : height }}>
        {children}
      </Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none", border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition: "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>{opt}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// Each step past the first carries its own denominator (`den`) for the
// in-bar percentage: RPC against Accounts, PTP and Smart Links against
// RPC, Kept against PTP. The share of Sent drops to the small line
// under the label. Steps with no `den` (Accounts itself) keep % of Sent.
//
// Labels are display-only renames — the underlying fields are unchanged.
// "RPC" is rpc_count; "Kept" is processed_ptp_count. Total PTP and
// Uncommitted are dropped from the funnel; both still have KPI tiles and
// table columns below.
// `accounts` comes from its own endpoint (/dashboard/hourly/accounts), not
// from the summary row. The step is ALWAYS rendered, including at 0, so a
// missing fetch shows as an empty bar rather than a vanished stage. At 0 its
// `den` is falsy and RPC falls back to % of Sent.
function Funnel({ summary, accounts = 0, pay }) {
  const sent      = safeNum(summary.texts_sent);
  // RPC is GROSS of opt-outs — rpc_count from the route is net, so they are
  // added back here. Opt-outs stay a tile of their own, now a subset of RPC.
  const rpc       = safeNum(summary.rpc_count) + safeNum(summary.optout_count);
  // Money steps from the payments route; summary is the fallback.
  const payments  = pay?.has ? pay.kept : safeNum(summary.processed_ptp_count);
  const committed = pay?.has ? pay.ptp
                             : safeNum(summary.processed_ptp_count) + safeNum(summary.promise_count);
  const smartlink = pay?.has ? pay.sl   : safeNum(summary.smartlink_count);
  const keptAmt   = pay?.has ? pay.keptAmt : safeNum(summary.processed_ptp_amount);
  const ptpAmount = pay?.has ? pay.ptpAmt
                             : safeNum(summary.processed_ptp_amount) + safeNum(summary.promise_amount);
  const slAmount  = pay?.has ? pay.slAmt : safeNum(summary.smartlink_amount);

  const base = sent || 1;
  const steps = [
    { label: "Sent",       val: sent,      color: C_SENT },
    { label: "Accounts",   val: accounts,  color: C_ACCOUNTS },
    { label: "RPC",        val: rpc,       color: C_RPC, den: accounts },
    { label: "PTP",        val: committed, color: C_PTP, den: rpc,       amount: ptpAmount },
    { label: "Same Day",       val: payments,  color: C_KEPT, den: committed, amount: keptAmt },
    { label: "Smart Links", val: smartlink, color: C_SL, den: rpc,       amount: slAmount },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5 }}>
      {steps.map((s, i) => {
        const pctOfSent = base > 0 ? (s.val / base) * 100 : 0;
        const denPct    = s.den > 0 ? (s.val / s.den) * 100 : null;
        const isFirst   = i === 0;
        // The fill is the number printed inside the bar, 1:1 — no
        // magnification. A step with its own `den` fills to its share of
        // that base; the rest fill to their share of Sent. Floored at 2%
        // so a near-zero step still shows a sliver of color.
        const shownPct  = isFirst ? 100 : (denPct != null ? denPct : pctOfSent);
        const fillW     = shownPct > 0 ? Math.min(100, Math.max(shownPct, 2)) : 0;
        return (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center" }}>
              <Box sx={{
                position: "relative", width: "100%", minWidth: 0,
                height: 46, background: "#f0f0f0", borderRadius: "4px",
                transition: "width .4s ease",
              }}>
                <Box sx={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${fillW}%`, background: s.color,
                  borderRadius: isFirst ? "4px" : "4px 0 0 4px",
                }} />
                {(
                  <Box sx={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 600, color: isFirst ? "#fff" : "#333",
                    pointerEvents: "none", whiteSpace: "nowrap",
                  }}>{isFirst
                    ? s.val.toLocaleString()
                    : denPct != null
                      ? `${denPct.toFixed(1)}%`
                      : `${pctOfSent.toFixed(1)}%`}</Box>
                )}
              </Box>
            </Box>

            <Box sx={{
              width: 90, flexShrink: 0, textAlign: "left",
              fontSize: 12, color: "text.secondary", lineHeight: 1.35,
            }}>
              <Box sx={{ color: s.color, fontWeight: 600 }}>{s.label}</Box>
              <Box sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
                {!isFirst && <span style={{ fontWeight: 500 }}>{s.val.toLocaleString()}</span>}
                {!isFirst && s.amount != null && <span> · <b style={{ color: s.color }}>{fmtCurrency(s.amount)}</b></span>}
              </Box>
              {denPct != null && (
                <Box sx={{ mt: 0.25, fontSize: 10, color: "text.disabled", whiteSpace: "nowrap" }}>
                  {pctOfSent.toFixed(1)}% of Sent
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function TargetCell({ target, actual }) {
  if (target == null) return <Box component="span" sx={{ color: "text.disabled" }}>—</Box>;
  const a = safeNum(actual);
  const ratio = target > 0 ? a / target : 0;
  let color = PINK;
  if (a >= target)        color = GREEN;
  else if (ratio >= 0.8)  color = AMBER;
  const gap = a - target;
  const gapText = a > 0
    ? `Δ ${gap >= 0 ? "+" : ""}${gap.toFixed(1)}`
    : "";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.1 }}>
      <Box component="span" sx={{ color: TARGET_C, fontWeight: 500 }}>
        {target.toFixed(0)}%
      </Box>
      {gapText && (
        <Box component="span" sx={{ color, fontSize: 10, fontWeight: 500, mt: "1px" }}>
          {gapText}
        </Box>
      )}
    </Box>
  );
}

// ── PTP by DPD bucket table ────────────────────────────────────────
// optout_pct is now REAL, not a placeholder. summary_ptp_hourly carries
// optout_count per (dpd_bucket, channel, campaign) — the same table and
// query that produces texts_sent and rpc_count — so this column no longer
// needs a separate live opt-out fetch and is never null for an active bucket.
const DPD_COLS = [
  { key: "dpd_bucket",          label: "DPD",          align: "left",  type: "pill" },
  { key: "texts_sent",          label: "Sent",         align: "right", type: "int"  },
  { key: "total_rpc_pct",       label: "RPC %",        align: "right", type: "pct",   color: C_RPC,
    derive: r => safeNum(r.texts_sent)
      ? ((safeNum(r.rpc_count) + safeNum(r.optout_count)) / safeNum(r.texts_sent)) * 100 : 0 },
  { key: "total_ptp_pct",       label: "PTP %",        align: "right", type: "pct",   color: C_PTP  },
  { key: "smartlinks_pct",      label: "SL %",         align: "right", type: "pct", color: C_SL   },
  { key: "optout_pct",          label: "Opt-out %",    align: "right", type: "pct", color: OPTOUT_C },
  { key: "rpc_count",           label: "RPC",          align: "right", type: "int", color: C_RPC,
    derive: r => safeNum(r.rpc_count) + safeNum(r.optout_count) },
  { key: "ptp_count",           label: "PTP",          align: "right", type: "int", color: C_PTP,
    derive: r => safeNum(r.processed_ptp_count) + safeNum(r.promise_count) },
  { key: "processed_ptp_count", label: "Kept",         align: "right", type: "int", color: C_KEPT },
  { key: "future_ptp_count",    label: "Uncommitted",  align: "right", type: "int", color: C_UNCOMMITTED },
  { key: "total_ptp_count",     label: "Total PTP",    align: "right", type: "int", color: AMBER  },
  { key: "smartlink_count",     label: "SmartLinks",   align: "right", type: "int", color: C_SL   },
  { key: "total_ptp_amount",    label: "Total PTP $",  align: "right", type: "cur", color: AMBER  },
];
const DPD_GRID = `1.1fr repeat(${DPD_COLS.length - 1}, 1fr)`;

function PtpByDpdTable({ data }) {
  const rows = useMemo(() => {
    const byBucket = {};
    (data || []).forEach(r => { byBucket[r.dpd_bucket] = r; });
    return DPD_BUCKETS.map(b => {
      const row = byBucket[b];
      if (row) return row;
      return {
        dpd_bucket: b, texts_sent: 0, rpc_count: 0, optout_count: 0,
        optout_pct: 0, processed_ptp_count: 0,
        processed_ptp_amount: 0, promise_count: 0, promise_amount: 0,
        future_ptp_count: 0, future_ptp_amount: 0,
        total_ptp_count: 0, total_ptp_amount: 0, smartlink_count: 0,
        smartlink_amount: 0, total_rpc_pct: 0, total_ptp_pct: 0,
        smartlinks_pct: 0,
        rpc_target: 0, ptp_target: 0,
      };
    });
  }, [data]);

  const totals = useMemo(() => {
    const t = rows.reduce((acc, r) => ({
      texts_sent:           acc.texts_sent           + safeNum(r.texts_sent),
      rpc_count:            acc.rpc_count            + safeNum(r.rpc_count),
      optout_count:         acc.optout_count         + safeNum(r.optout_count),
      processed_ptp_count:  acc.processed_ptp_count  + safeNum(r.processed_ptp_count),
      processed_ptp_amount: acc.processed_ptp_amount + safeNum(r.processed_ptp_amount),
      promise_count:        acc.promise_count        + safeNum(r.promise_count),
      promise_amount:       acc.promise_amount       + safeNum(r.promise_amount),
      future_ptp_count:     acc.future_ptp_count     + safeNum(r.future_ptp_count),
      future_ptp_amount:    acc.future_ptp_amount    + safeNum(r.future_ptp_amount),
      total_ptp_count:      acc.total_ptp_count      + safeNum(r.total_ptp_count),
      total_ptp_amount:     acc.total_ptp_amount     + safeNum(r.total_ptp_amount),
      smartlink_count:      acc.smartlink_count      + safeNum(r.smartlink_count),
      smartlink_amount:     acc.smartlink_amount     + safeNum(r.smartlink_amount),
    }), {
      texts_sent: 0, rpc_count: 0, optout_count: 0,
      processed_ptp_count: 0, processed_ptp_amount: 0,
      promise_count: 0, promise_amount: 0,
      future_ptp_count: 0, future_ptp_amount: 0, total_ptp_count: 0,
      total_ptp_amount: 0, smartlink_count: 0, smartlink_amount: 0,
    });
    return {
      ...t,
      // RPC totals are gross of opt-outs, matching the per-row derive.
      rpc_count:      t.rpc_count + t.optout_count,
      total_rpc_pct:  t.texts_sent ? ((t.rpc_count + t.optout_count) / t.texts_sent) * 100 : 0,
      total_ptp_pct:  t.texts_sent ? (t.total_ptp_count / t.texts_sent) * 100 : 0,
      smartlinks_pct: t.texts_sent ? (t.smartlink_count / t.texts_sent) * 100 : 0,
      optout_pct:     t.texts_sent ? (t.optout_count    / t.texts_sent) * 100 : 0,
      ptp_count:      t.processed_ptp_count + t.promise_count,
      rpc_target:     weightedTarget(rows, "rpc_target"),
      ptp_target:     weightedTarget(rows, "ptp_target"),
    };
  }, [rows]);

  const renderCell = (col, row) => {
    if (col.type === "pill") {
      const c = DPD_COLORS[row.dpd_bucket] || "#888";
      return (
        <Box component="span" sx={{
          display: "inline-block", px: "7px", py: "1px", borderRadius: "20px",
          fontSize: 11, fontWeight: 500,
          bgcolor: `${c}18`, color: c, border: `0.5px solid ${c}44`,
        }}>{row.dpd_bucket}</Box>
      );
    }
    if (col.type === "target") {
      return <TargetCell target={row[col.key]} actual={row[col.actualKey]} />;
    }
    const v = safeNum(col.derive ? col.derive(row) : row[col.key]);
    const empty = v === 0;
    const color = empty ? "text.disabled" : (col.color || "text.primary");
    const text = col.type === "cur" ? fmtCurrency(v)
              : col.type === "pct" ? (empty ? "—" : fmtPct(v))
              : (empty ? "—" : v.toLocaleString());
    return <Box component="span" sx={{ color, fontWeight: empty ? 400 : 500 }}>{text}</Box>;
  };

  const gridSx = { display: "grid", gridTemplateColumns: DPD_GRID, px: 2 };

  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto", mb: 1.5 }}>
      <Box sx={{ ...gridSx, py: 1, background: "#f7f7f7", borderBottom: "0.5px solid",
        borderColor: "divider", fontSize: 11, fontWeight: 500,
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {DPD_COLS.map(col => (
          <Box key={col.key} sx={{ textAlign: col.align, color: "text.secondary" }}>{col.label}</Box>
        ))}
      </Box>
      {rows.map((row, i) => (
        <Box key={row.dpd_bucket} sx={{ ...gridSx, py: "7px", fontSize: 12,
          borderBottom: "0.5px solid", borderColor: "divider",
          background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
          "&:hover": { background: "rgba(55,138,221,0.06)" } }}>
          {DPD_COLS.map(col => (
            <Box key={col.key} sx={{ display: "flex", alignItems: "center",
              justifyContent: col.align === "right" ? "flex-end" : "flex-start" }}>
              {renderCell(col, row)}
            </Box>
          ))}
        </Box>
      ))}
      <Box sx={{ ...gridSx, py: "8px", fontSize: 12, background: "#f0f5ff", borderTop: `1px solid ${BLUE}33` }}>
        <Box sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>Total</Box>
        {DPD_COLS.slice(1).map(col => {
          if (col.type === "target") {
            return (
              <Box key={col.key} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <TargetCell target={totals[col.key]} actual={totals[col.actualKey]} />
              </Box>
            );
          }
          const v = safeNum(totals[col.key]);
          const text = col.type === "cur" ? fmtCurrency(v)
                    : col.type === "pct" ? fmtPct(v)
                    : v.toLocaleString();
          return (
            <Box key={col.key} sx={{ display: "flex", justifyContent: "flex-end",
              color: col.color || BLUE, fontWeight: 700 }}>{text}</Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── PTP by DPD day table ────────────────────────────────────────────
const BY_DAY_DPD_COLS = [
  { key: "dpd_day",             label: "DPD Day",      align: "left",  type: "day_pill" },
  { key: "texts_sent",          label: "Sent",         align: "right", type: "int"  },
  { key: "total_rpc_pct",       label: "RPC %",        align: "right", type: "pct",   color: C_RPC,
    derive: r => safeNum(r.texts_sent)
      ? ((safeNum(r.rpc_count) + safeNum(r.optout_count)) / safeNum(r.texts_sent)) * 100 : 0 },
  { key: "total_ptp_pct",       label: "PTP %",        align: "right", type: "pct",   color: C_PTP  },
  { key: "smartlinks_pct",      label: "SL %",         align: "right", type: "pct", color: C_SL   },
  { key: "rpc_count",           label: "RPC",          align: "right", type: "int", color: C_RPC,
    derive: r => safeNum(r.rpc_count) + safeNum(r.optout_count) },
  { key: "ptp_count",           label: "PTP",          align: "right", type: "int", color: C_PTP,
    derive: r => safeNum(r.processed_ptp_count) + safeNum(r.promise_count) },
  { key: "processed_ptp_count", label: "Kept",         align: "right", type: "int", color: C_KEPT },
  { key: "future_ptp_count",    label: "Uncommitted",  align: "right", type: "int", color: C_UNCOMMITTED },
  { key: "total_ptp_count",     label: "Total PTP",    align: "right", type: "int", color: AMBER  },
  { key: "smartlink_count",     label: "SmartLinks",   align: "right", type: "int", color: C_SL   },
  { key: "total_ptp_amount",    label: "Total PTP $",  align: "right", type: "cur", color: AMBER  },
];
const BY_DAY_DPD_GRID = `90px repeat(${BY_DAY_DPD_COLS.length - 1}, 1fr)`;

function PtpByDpdDayTable({ data }) {
  const rows = useMemo(() => data || [], [data]);

  const totals = useMemo(() => {
    const t = rows.reduce((acc, r) => ({
      texts_sent:           acc.texts_sent           + safeNum(r.texts_sent),
      rpc_count:            acc.rpc_count            + safeNum(r.rpc_count),
      optout_count:         acc.optout_count         + safeNum(r.optout_count),
      processed_ptp_count:  acc.processed_ptp_count  + safeNum(r.processed_ptp_count),
      promise_count:        acc.promise_count        + safeNum(r.promise_count),
      future_ptp_count:     acc.future_ptp_count     + safeNum(r.future_ptp_count),
      total_ptp_count:      acc.total_ptp_count      + safeNum(r.total_ptp_count),
      total_ptp_amount:     acc.total_ptp_amount     + safeNum(r.total_ptp_amount),
      smartlink_count:      acc.smartlink_count      + safeNum(r.smartlink_count),
    }), {
      texts_sent: 0, rpc_count: 0, optout_count: 0, processed_ptp_count: 0,
      promise_count: 0,
      future_ptp_count: 0, total_ptp_count: 0, total_ptp_amount: 0,
      smartlink_count: 0,
    });
    return {
      ...t,
      rpc_count:      t.rpc_count + t.optout_count,
      total_rpc_pct:  t.texts_sent ? ((t.rpc_count + t.optout_count) / t.texts_sent) * 100 : 0,
      total_ptp_pct:  t.texts_sent ? (t.total_ptp_count / t.texts_sent) * 100 : 0,
      smartlinks_pct: t.texts_sent ? (t.smartlink_count / t.texts_sent) * 100 : 0,
      ptp_count:      t.processed_ptp_count + t.promise_count,
      rpc_target:     weightedTarget(rows, "rpc_target"),
      ptp_target:     weightedTarget(rows, "ptp_target"),
    };
  }, [rows]);

  const renderCell = (col, row) => {
    if (col.type === "day_pill") {
      const c = DPD_COLORS[row.dpd_bucket] || "#888";
      const label = row.dpd_day === 90 ? "90+" : `Day ${row.dpd_day}`;
      return (
        <Box component="span" sx={{
          display: "inline-block", px: "7px", py: "1px", borderRadius: "20px",
          fontSize: 11, fontWeight: 500,
          bgcolor: `${c}18`, color: c, border: `0.5px solid ${c}44`,
        }}>{label}</Box>
      );
    }
    if (col.type === "target") {
      return <TargetCell target={row[col.key]} actual={row[col.actualKey]} />;
    }
    const raw = col.derive ? col.derive(row) : row[col.key];
    const v = safeNum(raw);
    const empty = v === 0;
    const color = empty ? "text.disabled" : (col.color || "text.primary");
    const text = col.type === "cur" ? fmtCurrency(v)
              : col.type === "pct" ? (empty ? "—" : fmtPct(v))
              : (empty ? "—" : v.toLocaleString());
    return <Box component="span" sx={{ color, fontWeight: empty ? 400 : 500 }}>{text}</Box>;
  };

  const gridSx = { display: "grid", gridTemplateColumns: BY_DAY_DPD_GRID, px: 2 };

  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2,
      overflowX: "auto", mb: 1.5,
      maxHeight: 480, overflowY: "auto" }}>
      <Box sx={{ ...gridSx, py: 1, background: "#f7f7f7", borderBottom: "0.5px solid",
        borderColor: "divider", fontSize: 11, fontWeight: 500,
        textTransform: "uppercase", letterSpacing: "0.05em",
        position: "sticky", top: 0, zIndex: 1 }}>
        {BY_DAY_DPD_COLS.map(col => (
          <Box key={col.key} sx={{ textAlign: col.align, color: "text.secondary" }}>{col.label}</Box>
        ))}
      </Box>
      {rows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.disabled", fontSize: 12 }}>
          No DPD-day data for the selected period.
        </Box>
      )}
      {rows.map((row, i) => (
        <Box key={row.dpd_day} sx={{ ...gridSx, py: "7px", fontSize: 12,
          borderBottom: "0.5px solid", borderColor: "divider",
          background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
          "&:hover": { background: "rgba(55,138,221,0.06)" } }}>
          {BY_DAY_DPD_COLS.map(col => (
            <Box key={col.key} sx={{ display: "flex", alignItems: "center",
              justifyContent: col.align === "right" ? "flex-end" : "flex-start" }}>
              {renderCell(col, row)}
            </Box>
          ))}
        </Box>
      ))}
      {rows.length > 0 && (
        <Box sx={{ ...gridSx, py: "8px", fontSize: 12, background: "#f0f5ff",
          borderTop: `1px solid ${BLUE}33`,
          position: "sticky", bottom: 0 }}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>Total</Box>
          {BY_DAY_DPD_COLS.slice(1).map(col => {
            if (col.type === "target") {
              return (
                <Box key={col.key} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <TargetCell target={totals[col.key]} actual={totals[col.actualKey]} />
                </Box>
              );
            }
            const v = safeNum(totals[col.key]);
            const text = col.type === "cur" ? fmtCurrency(v)
                      : col.type === "pct" ? fmtPct(v)
                      : v.toLocaleString();
            return (
              <Box key={col.key} sx={{ display: "flex", justifyContent: "flex-end",
                color: col.color || BLUE, fontWeight: 700 }}>{text}</Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ── PTP by day (date) table ────────────────────────────────────────
const DAY_COLS = [
  { key: "summary_date",        label: "Date",         align: "left",  type: "date" },
  { key: "texts_sent",          label: "Sent",         align: "right", type: "int"  },
  { key: "total_rpc_pct",       label: "RPC %",        align: "right", type: "pct",   color: C_RPC,
    derive: r => safeNum(r.texts_sent)
      ? ((safeNum(r.rpc_count) + safeNum(r.optout_count)) / safeNum(r.texts_sent)) * 100 : 0 },
  { key: "total_ptp_pct",       label: "PTP %",        align: "right", type: "pct",   color: C_PTP  },
  { key: "smartlinks_pct",      label: "SL %",         align: "right", type: "pct", color: C_SL   },
  { key: "rpc_count",           label: "RPC",          align: "right", type: "int", color: C_RPC,
    derive: r => safeNum(r.rpc_count) + safeNum(r.optout_count) },
  { key: "ptp_count",           label: "PTP",          align: "right", type: "int", color: C_PTP,
    derive: r => safeNum(r.processed_ptp_count) + safeNum(r.promise_count) },
  { key: "processed_ptp_count", label: "Kept",         align: "right", type: "int", color: C_KEPT },
  { key: "future_ptp_count",    label: "Uncommitted",  align: "right", type: "int", color: C_UNCOMMITTED },
  { key: "total_ptp_count",     label: "Total PTP",    align: "right", type: "int", color: AMBER  },
  { key: "smartlink_count",     label: "SmartLinks",   align: "right", type: "int", color: C_SL   },
  { key: "total_ptp_amount",    label: "Total PTP $",  align: "right", type: "cur", color: AMBER  },
];
const DAY_GRID = `90px repeat(${DAY_COLS.length - 1}, 1fr)`;

function PtpByDayTable({ rawData }) {
  const rows = useMemo(() => {
    const byDate = new Map();
    (rawData || []).forEach(r => {
      const date = r.summary_date;
      if (!byDate.has(date)) {
        byDate.set(date, {
          summary_date: date,
          texts_sent: 0, rpc_count: 0, optout_count: 0,
          processed_ptp_count: 0, promise_count: 0, future_ptp_count: 0,
          total_ptp_count: 0,    total_ptp_amount: 0,
          smartlink_count: 0,
          _dpd_breakouts: [],
        });
      }
      const agg = byDate.get(date);
      agg.texts_sent          += safeNum(r.texts_sent);
      agg.rpc_count           += safeNum(r.rpc_count);
      agg.optout_count        += safeNum(r.optout_count);
      agg.processed_ptp_count += safeNum(r.processed_ptp_count);
      agg.promise_count       += safeNum(r.promise_count);
      agg.future_ptp_count    += safeNum(r.future_ptp_count);
      agg.total_ptp_count     += safeNum(r.total_ptp_count);
      agg.total_ptp_amount    += safeNum(r.total_ptp_amount);
      agg.smartlink_count     += safeNum(r.smartlink_count);
      agg._dpd_breakouts.push({
        dpd_bucket: r.dpd_bucket,
        texts_sent: r.texts_sent,
        rpc_target: r.rpc_target,
        ptp_target: r.ptp_target,
      });
    });

    return Array.from(byDate.values()).map(r => ({
      ...r,
      summary_date:    fmtDate(r.summary_date),
      total_rpc_pct:   r.texts_sent ? ((r.rpc_count + r.optout_count) / r.texts_sent) * 100 : 0,
      total_ptp_pct:   r.texts_sent ? (r.total_ptp_count / r.texts_sent) * 100 : 0,
      smartlinks_pct:  r.texts_sent ? (r.smartlink_count / r.texts_sent) * 100 : 0,
      rpc_target:      weightedTarget(r._dpd_breakouts, "rpc_target"),
      ptp_target:      weightedTarget(r._dpd_breakouts, "ptp_target"),
    }));
  }, [rawData]);

  const totals = useMemo(() => {
    const t = rows.reduce((acc, r) => ({
      texts_sent:          acc.texts_sent          + safeNum(r.texts_sent),
      rpc_count:           acc.rpc_count           + safeNum(r.rpc_count),
      optout_count:        acc.optout_count        + safeNum(r.optout_count),
      processed_ptp_count: acc.processed_ptp_count + safeNum(r.processed_ptp_count),
      promise_count:       acc.promise_count       + safeNum(r.promise_count),
      future_ptp_count:    acc.future_ptp_count    + safeNum(r.future_ptp_count),
      total_ptp_count:     acc.total_ptp_count     + safeNum(r.total_ptp_count),
      total_ptp_amount:    acc.total_ptp_amount    + safeNum(r.total_ptp_amount),
      smartlink_count:     acc.smartlink_count     + safeNum(r.smartlink_count),
      _all_breakouts: [...acc._all_breakouts, ...(r._dpd_breakouts || [])],
    }), {
      texts_sent: 0, rpc_count: 0, optout_count: 0, processed_ptp_count: 0,
      promise_count: 0,
      future_ptp_count: 0, total_ptp_count: 0, total_ptp_amount: 0,
      smartlink_count: 0, _all_breakouts: [],
    });
    return {
      ...t,
      rpc_count:      t.rpc_count + t.optout_count,
      total_rpc_pct:  t.texts_sent ? ((t.rpc_count + t.optout_count) / t.texts_sent) * 100 : 0,
      total_ptp_pct:  t.texts_sent ? (t.total_ptp_count / t.texts_sent) * 100 : 0,
      smartlinks_pct: t.texts_sent ? (t.smartlink_count / t.texts_sent) * 100 : 0,
      ptp_count:      t.processed_ptp_count + t.promise_count,
      rpc_target:     weightedTarget(t._all_breakouts, "rpc_target"),
      ptp_target:     weightedTarget(t._all_breakouts, "ptp_target"),
    };
  }, [rows]);

  const renderCell = (col, row) => {
    if (col.type === "date") {
      return <Box component="span" sx={{ color: "text.secondary", fontSize: 11 }}>{row[col.key]}</Box>;
    }
    if (col.type === "target") {
      return <TargetCell target={row[col.key]} actual={row[col.actualKey]} />;
    }
    const v = safeNum(col.derive ? col.derive(row) : row[col.key]);
    const empty = v === 0;
    const color = empty ? "text.disabled" : (col.color || "text.primary");
    const text = col.type === "cur" ? fmtCurrency(v)
              : col.type === "pct" ? (empty ? "—" : fmtPct(v))
              : (empty ? "—" : v.toLocaleString());
    return <Box component="span" sx={{ color, fontWeight: empty ? 400 : 500 }}>{text}</Box>;
  };

  const gridSx = { display: "grid", gridTemplateColumns: DAY_GRID, px: 2 };

  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto", mb: 1.5 }}>
      <Box sx={{ ...gridSx, py: 1, background: "#f7f7f7", borderBottom: "0.5px solid",
        borderColor: "divider", fontSize: 11, fontWeight: 500,
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {DAY_COLS.map(col => (
          <Box key={col.key} sx={{ textAlign: col.align, color: "text.secondary" }}>{col.label}</Box>
        ))}
      </Box>
      {rows.map((row, i) => (
        <Box key={row.summary_date + i} sx={{ ...gridSx, py: "7px", fontSize: 12,
          borderBottom: "0.5px solid", borderColor: "divider",
          background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
          "&:hover": { background: "rgba(55,138,221,0.06)" } }}>
          {DAY_COLS.map(col => (
            <Box key={col.key} sx={{ display: "flex", alignItems: "center",
              justifyContent: col.align === "right" ? "flex-end" : "flex-start" }}>
              {renderCell(col, row)}
            </Box>
          ))}
        </Box>
      ))}
      <Box sx={{ ...gridSx, py: "8px", fontSize: 12, background: "#f0f5ff", borderTop: `1px solid ${BLUE}33` }}>
        <Box sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>Total</Box>
        {DAY_COLS.slice(1).map(col => {
          if (col.type === "target") {
            return (
              <Box key={col.key} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <TargetCell target={totals[col.key]} actual={totals[col.actualKey]} />
              </Box>
            );
          }
          const v = safeNum(totals[col.key]);
          const text = col.type === "cur" ? fmtCurrency(v)
                    : col.type === "pct" ? fmtPct(v)
                    : v.toLocaleString();
          return (
            <Box key={col.key} sx={{ display: "flex", justifyContent: "flex-end",
              color: col.color || BLUE, fontWeight: 700 }}>{text}</Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function PtpDashboard({ channel }) {
  // Day / 30D on open: a daily series over a month is the view people
  // land on this page for. Week/90D smoothed away the movement the
  // page exists to show, and had every visit start with two clicks.
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  const [summary,       setSummary]       = useState({});
  const [byDpd,         setByDpd]         = useState([]);
  const [byDpdDay,      setByDpdDay]      = useState([]);
  const [byDayRaw,      setByDayRaw]      = useState([]);
  const [accounts,      setAccounts]      = useState(0);
  const [paySummary,    setPaySummary]    = useState([]);
  const [prevSummary,   setPrevSummary]   = useState({});

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [editorOpen, setEditorOpen] = useState(false);

  const { timeZone, tzReady } = useClientTimeZone();

  const channelParam = (channel || "texts").toLowerCase();

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset, timeZone),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel:     channelParam,
  }), [rangePreset, campaign, gran, channelParam, timeZone]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel:     channelParam,
  }), [params.startDate, params.endDate, campaign, gran, channelParam]);

  const loadAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      // Accounts: current period only — one funnel step, no delta.
      const [s, d, dd, day, acc, pay, ps] = await Promise.all([
        getPtpSummary(params),
        getPtpByDpd(params),
        getPtpByDpdDay(params).catch(() => []),
        getPtpByDay(params),
        getAccounts(params).catch(() => ({})),
        getPaymentsSummary(params).catch(() => []),
        getPtpSummary(prevParams).catch(() => ({})),
      ]);
      setSummary(s        || {});
      setByDpd(d          || []);
      setByDpdDay(dd      || []);
      setByDayRaw(day     || []);
      setAccounts(safeNum(acc?.account_count));
      setPaySummary(pay  || []);
      setPrevSummary(ps   || {});
    } catch (e) {
      console.error("PTP load error:", e);
      setError("Failed to load PTP data");
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  // Gated on tzReady — firing before the client config lands would request a
  // UTC-derived window and then immediately refetch with the real zone.
  useEffect(() => { if (tzReady) loadAll(); }, [loadAll, tzReady]);

  const kpis = useMemo(() => {
    const c = summary; const p = prevSummary;
    const pctOpt  = { goodWhenUp: true, fmtVal: v => fmtPct(v) };
    const cashOpt = { goodWhenUp: true, fmtVal: v => fmtCurrency(v) };

    // optout_count/optout_pct come straight off the summary row now — same
    // query as texts_sent and rpc_count, no separate opt-out fetch.
    const sent      = safeNum(c.texts_sent);
    const optoutCnt = safeNum(c.optout_count);
    const optoutPct = sent > 0 ? (optoutCnt / sent) * 100 : 0;
    const prevSent      = safeNum(p.texts_sent);
    const prevOptoutCnt = safeNum(p.optout_count);
    const prevOptoutPct = prevSent > 0 ? (prevOptoutCnt / prevSent) * 100 : 0;

    const rpcTargetWeighted = weightedTarget(byDpd, "rpc_target");
    const ptpTargetWeighted = weightedTarget(byDpd, "ptp_target");

    // Gross of opt-outs, matching the funnel and the tiles.
    const rpc            = safeNum(c.rpc_count) + optoutCnt;
    const prevRpc        = safeNum(p.rpc_count) + prevOptoutCnt;
    const rpcPct         = sent     > 0 ? (rpc     / sent)     * 100 : 0;
    const prevRpcPct     = prevSent > 0 ? (prevRpc / prevSent) * 100 : 0;

    // PTP = Kept + promises, for counts and $ alike. Kept reads against it;
    // PTP itself reads against RPC.
    const ptpCount     = safeNum(c.processed_ptp_count)  + safeNum(c.promise_count);
    const ptpAmt       = safeNum(c.processed_ptp_amount) + safeNum(c.promise_amount);
    const prevPtpCount = safeNum(p.processed_ptp_count)  + safeNum(p.promise_count);
    const ptpPct       = rpc      > 0 ? (ptpCount     / rpc)      * 100 : 0;
    const prevPtpPct   = prevRpc  > 0 ? (prevPtpCount / prevRpc)  * 100 : 0;
    const keptPct      = ptpCount > 0 ? (safeNum(c.processed_ptp_count) / ptpCount) * 100 : 0;
    // Smart Links read against RPC, not PTP: a smart link is its own event
    // code, not a component of PTP, so PTP is not a base that contains it.
    const slPct        = rpc     > 0 ? (safeNum(c.smartlink_count) / rpc)     * 100 : 0;
    const prevSlPct    = prevRpc > 0 ? (safeNum(p.smartlink_count) / prevRpc) * 100 : 0;
    const payPct          = rpc > 0 ? (safeNum(c.processed_ptp_count) / rpc) * 100 : 0;
    const committedPct    = rpc > 0 ? (safeNum(c.promise_count)       / rpc) * 100 : 0;
    const uncommittedPct  = rpc > 0 ? (safeNum(c.future_ptp_count)    / rpc) * 100 : 0;
    const totalPtpPctVsRpc = rpc > 0 ? (safeNum(c.total_ptp_count)    / rpc) * 100 : 0;
    const prevPayPct          = prevRpc > 0 ? (safeNum(p.processed_ptp_count) / prevRpc) * 100 : 0;
    const prevCommittedPct    = prevRpc > 0 ? (safeNum(p.promise_count)       / prevRpc) * 100 : 0;
    const prevUncommittedPct  = prevRpc > 0 ? (safeNum(p.future_ptp_count)    / prevRpc) * 100 : 0;
    const prevTotalPtpPctVsRpc = prevRpc > 0 ? (safeNum(p.total_ptp_count)    / prevRpc) * 100 : 0;

    return {
      optoutPct,
      rpc, rpcPct,
      ptpCount, ptpAmt, ptpPct, keptPct, slPct,
      ptpVsRpcPctDelta: buildDelta(ptpPct, prevPtpPct, pctOpt),
      weightedRpcTarget: rpcTargetWeighted,
      weightedPtpTarget: ptpTargetWeighted,
      payPct,
      committedPct,
      uncommittedPct,
      totalPtpPctVsRpc,
      sentDelta:        buildDelta(c.texts_sent,           p.texts_sent),
      rpcDelta:         buildDelta(rpc,                    prevRpc),
      rpcPctDelta:      buildDelta(rpcPct,                 prevRpcPct,             pctOpt),
      payDelta:         buildDelta(c.processed_ptp_count,  p.processed_ptp_count),
      payAmtDelta:      buildDelta(c.processed_ptp_amount, p.processed_ptp_amount, cashOpt),
      payPctDelta:      buildDelta(payPct,                 prevPayPct,             pctOpt),
      committedDelta:    buildDelta(c.promise_count,        p.promise_count),
      committedAmtDelta: buildDelta(c.promise_amount,       p.promise_amount,       cashOpt),
      committedPctDelta: buildDelta(committedPct,           prevCommittedPct,       pctOpt),
      uncommittedDelta:    buildDelta(c.future_ptp_count,   p.future_ptp_count),
      uncommittedAmtDelta: buildDelta(c.future_ptp_amount,  p.future_ptp_amount,    cashOpt),
      uncommittedPctDelta: buildDelta(uncommittedPct,       prevUncommittedPct,     pctOpt),
      totalDelta:       buildDelta(c.total_ptp_count,      p.total_ptp_count),
      totalAmtDelta:    buildDelta(c.total_ptp_amount,     p.total_ptp_amount,     cashOpt),
      ptpPctDelta:              buildDelta(ptpPct,              prevPtpPct,           pctOpt),
      totalPtpPctVsSentDelta:   buildDelta(c.total_ptp_pct,     p.total_ptp_pct,      pctOpt),
      slDelta:          buildDelta(c.smartlink_count,      p.smartlink_count),
      slAmtDelta:       buildDelta(c.smartlink_amount,     p.smartlink_amount,     cashOpt),
      slPctDelta:       buildDelta(slPct,                  prevSlPct,              pctOpt),
      optoutPctDelta:   buildDelta(optoutPct,               prevOptoutPct,          {
        goodWhenUp: false, fmtVal: v => fmtPct(v),
      }),
    };
  }, [summary, prevSummary, byDpd]);

  const chartData = useMemo(() => {
    const byDate = new Map();
    (byDayRaw || []).forEach(r => {
      const date = r.summary_date;
      if (!byDate.has(date)) {
        byDate.set(date, { date, sent: 0, pay: 0, committed: 0, uncommitted: 0, sl: 0, rpc: 0, totalPtp: 0 });
      }
      const a = byDate.get(date);
      a.sent        += safeNum(r.texts_sent);
      a.rpc         += safeNum(r.rpc_count) + safeNum(r.optout_count);
      a.pay         += safeNum(r.processed_ptp_count);
      a.committed   += safeNum(r.promise_count);
      a.uncommitted += safeNum(r.future_ptp_count);
      a.sl          += safeNum(r.smartlink_count);
      a.totalPtp    += safeNum(r.total_ptp_count);
    });
    return Array.from(byDate.values()).map(r => ({
      date:        fmtAxisDate(r.date, gran),
      pay:         r.pay,
      committed:   r.committed,
      uncommitted: r.uncommitted,
      sl:          r.sl,
      // Two rates, two different denominators, deliberately kept apart:
      //   ptpVsRpcPct  promise_count / responses  — the "PTP" tile in the
      //                                             top band
      //   totalPct     total_ptp     / sent       — the "Total PTP" tile in
      //                                             Engagement
      // Charting one under the other's band draws a line that disagrees with
      // the card above it.
      // Carried so the dotted rules can pool rather than average percentages.
      sent:        r.sent,
      rpc:         r.rpc,
      ptpVsRpcPct: r.rpc  ? ((r.pay + r.committed) / r.rpc) * 100 : 0,
      ptp:         r.pay + r.committed,
      totalPct:    r.sent ? (r.totalPtp  / r.sent) * 100 : 0,
      rpcPct:      r.sent ? (r.rpc       / r.sent) * 100 : 0,
      // Raw count for the Engagement chart — the "Total PTP" tile's own
      // number, so the line and the card read the same.
      totalPtp:    r.totalPtp,
    }));
  }, [byDayRaw, gran]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };
  const tzLabel      = zoneAbbr(timeZone);
  const ptpAmt       = useMemo(() => ptpAmountStats(paySummary), [paySummary]);
  const payTotals    = useMemo(() => paymentsTotals(paySummary), [paySummary]);

  // PTP $ over time — Kept + promises per bucket, from the same payments rows
  // the KPI strip aggregates, so the chart and the row cannot disagree.
  const ptpAmtSeries = useMemo(() =>
    [...(paySummary || [])]
      .sort((a, b) => String(a.summary_date).localeCompare(String(b.summary_date)))
      .map(r => ({
        date: fmtAxisDate(r.summary_date, gran),
        amt:  safeNum(r.payment_total) + safeNum(r.promise_total),
      }))
  , [paySummary, gran]);
  const avgPtpAmt = activeMean(ptpAmtSeries, "amt");

  // Averages behind each chart's dotted rule. Rates pooled, counts the mean
  // of the buckets that had activity.
  const avgPtpVsRpc  = pooledPct(chartData, "ptp", "rpc");
  const avgTotalPtp  = activeMean(chartData, "totalPtp");
  const avgTotalPct  = pooledPct(chartData, "totalPtp", "sent");
  // ── THE RANGE THE PILLS RESOLVED TO ──
  // Appended to every chart title and the funnel, the way the scorecard does
  // it. The pills say "90D"; only this says which 90 days, and a screenshot
  // or a PDF export carries no pill state at all.
  const rangeLabel = `${fmtDate(params.startDate)} – ${fmtDate(params.endDate)}`;
  const titleOf    = (t) => (rangeLabel ? `${t} · ${rangeLabel}` : t);



  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup
          label="Date range" options={RANGE_OPTIONS}
          value={rangePreset} onChange={setRangePreset}
          groups={[3, 4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Campaign
          </Typography>
          <Select value={campaign} onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}>
            {campaigns.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ ml: "auto", alignSelf: "flex-end", display: "flex",
          alignItems: "center", gap: 1.5 }}>
          <Box sx={{ fontSize: 11, color: "text.secondary" }}>
            Times shown in {tzLabel}
          </Box>
          {/* Targets are hidden for now — off the tiles, off all three tables,
              and this is the way back in. The editor still writes to
              ptp_target_buckets and the route still serves the view, so
              re-enabling is uncommenting this block and restoring the two
              `target={{...}}` props plus the two target columns.
          <Box
            onClick={() => setEditorOpen(true)}
            sx={{
              px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
              cursor: "pointer", userSelect: "none",
              border: "0.5px solid", borderColor: BLUE,
              background: "transparent", color: BLUE, fontWeight: 500,
              display: "inline-flex", alignItems: "center", gap: "6px",
              transition: "all .15s",
              "&:hover": { background: "rgba(55,138,221,0.08)" },
            }}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>⚙</span>
            Edit Targets
          </Box>
          */}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading PTP data…
        </Box>
      )}

      {!loading && (
        <>
          {/* The page's KPI row: Total / Avg / Max / Min of PTP $. Counts and
              rates are in the funnel beside the chart below, so they are not
              repeated here. Max/Min come from the payments route — the hourly
              summary carries sums only. */}
          <SectionTitle>PTP $</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            <MiniStat label="Total" value={fmtCurrency(ptpAmt.total)} color={C_PTP} />
            <MiniStat label="Avg"   value={fmtCurrency(ptpAmt.avg)}   color={C_PTP} />
            <MiniStat label="Max"   value={fmtCurrency(ptpAmt.max)}   color={C_PTP} />
            <MiniStat label="Min"   value={fmtCurrency(ptpAmt.min)}   color={C_PTP} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 1.5, mb: 0.5 }}>
            {/* Was "Response % over time" — the Trends dashboard's headline
                chart repeated verbatim. This band is about what responders
                went on to do, so the line is PTP measured against responses:
                the same number as the "PTP" tile directly above it. */}
            <ChartCard title={titleOf("PTP % of RPC")} right={`avg ${fmtPct(avgPtpVsRpc, 1)}`} height={340}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ptpRpcPctGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_PTP} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_PTP} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtPct(v), n]} />
                  <ReferenceLine y={avgPtpVsRpc} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="ptpVsRpcPct" name="PTP % of RPC"
                    stroke={C_PTP} fill="url(#ptpRpcPctGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="ptpVsRpcPct" position="top" offset={8}
                      fontSize={10} fill={C_PTP} formatter={v => fmtPct(v, 1)} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={titleOf("Conversion funnel")} height="auto">
              <Funnel summary={summary} accounts={accounts} pay={payTotals} />
            </ChartCard>
          </Box>

          <Box sx={{
            mt: 1, mb: 1.5, p: "10px 14px", borderRadius: 2,
            border: "0.5px solid", borderColor: `${AMBER}55`,
            background: `${AMBER}0F`, fontSize: 12, color: "text.secondary", lineHeight: 1.5,
          }}>
            <b style={{ color: AMBER }}>Note:</b>{" "}
            <b>RPC</b> is the count of customers who responded to the texts, including
            opt-outs — a STOP is still a reply, and it is counted here as one. <b>Kept</b> are immediate payments processed through PNM, and{" "}
            <b>PTP</b> is Kept plus the future payments scheduled through PNM — the base Kept
            is measured against. Sent, RPC, Opt-outs and the PTP figures on this page all come
            from one query, bucketed in {tzLabel}.
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("PTP $")} right={`avg ${fmtCurrency(avgPtpAmt)}`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={ptpAmtSeries} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ptpAmtGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C_PTP} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C_PTP} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} tickFormatter={v => fmtCurrency(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtCurrency(v), n]} />
                  <ReferenceLine y={avgPtpAmt} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="amt" name="PTP $"
                    stroke={C_PTP} fill="url(#ptpAmtGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="amt" position="top" offset={8}
                      fontSize={10} fill={C_PTP} formatter={v => fmtCurrency(v)} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            {/* Counts, not a rate. The rate against responses is already the
                top band's chart, and the rate against sent said little that
                the Total PTP tile doesn't. Volume over the window is the
                thing this band was missing. */}
            <ChartCard title={titleOf("Total PTP count")} right={`avg ${Math.round(avgTotalPtp).toLocaleString()}`} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ptpCountGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={AMBER} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={AMBER} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle}
                    formatter={(v, n) => [safeNum(v).toLocaleString(), n]} />
                  <ReferenceLine y={avgTotalPtp} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="totalPtp" name="Total PTP"
                    stroke={AMBER} fill="url(#ptpCountGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="totalPtp" position="top" offset={8}
                      fontSize={10} fill={AMBER} formatter={v => safeNum(v).toLocaleString()} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          <Box sx={{
            mb: 1.5, p: "10px 14px", borderRadius: 2,
            border: "0.5px solid", borderColor: `${AMBER}55`,
            background: `${AMBER}0F`, fontSize: 12, color: "text.secondary", lineHeight: 1.5,
          }}>
            <b style={{ color: AMBER }}>Note:</b>{" "}
            <b>Kept</b> are immediate payments processed through PNM and <b>PTP</b> is Kept
            plus the future payments scheduled through PNM. <b>Uncommitted</b> are customers
            who were given the payment method but did not select one to process it.
          </Box>

          <SectionTitle>PTP by DPD bucket</SectionTitle>
          <PtpByDpdTable data={byDpd} />

          <SectionTitle>PTP by DPD day</SectionTitle>
          <PtpByDpdDayTable data={byDpdDay} />

          <SectionTitle>PTP by {gran.toLowerCase()}</SectionTitle>
          <PtpByDayTable rawData={byDayRaw} />
        </>
      )}

      {/* Paired with the commented Edit Targets button above.
      <PtpTargetEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={loadAll}
      />
      */}
    </Box>
  );
}